import { Typography, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { CHARGE_TYPE } from '@woovi/shared';
import { AccountInfoLabel, BoxFlex } from '@woovi/ui';
import { convertToMoney, maskMoney } from '@woovi/utils';

import type { PaymentLinkCompletedQrCodeValue_charge$key } from './__generated__/PaymentLinkCompletedQrCodeValue_charge.graphql.ts';

type PaymentLinkCompletedQrCodeValueProps = {
  charge: PaymentLinkCompletedQrCodeValue_charge$key;
};
const PaymentLinkCompletedQrCodeValue = (
  props: PaymentLinkCompletedQrCodeValueProps,
): ReactNode => {
  const { t } = useTranslation();
  const charge = useFragment<PaymentLinkCompletedQrCodeValue_charge$key>(
    graphql`
      fragment PaymentLinkCompletedQrCodeValue_charge on Charge {
        id
        discount
        value
        valuePaid
        valueWithDiscount
        chargeIntent {
          id
        }
        type
      }
    `,
    props.charge,
  );

  if (charge.chargeIntent?.id) {
    return null;
  }

  const hasDiscount = charge?.discount > 0;

  if (
    charge.type !== CHARGE_TYPE.DYNAMIC &&
    charge.type !== CHARGE_TYPE.OVERDUE
  ) {
    return (
      <AccountInfoLabel
        label={t('Amount paid')}
        text={convertToMoney(charge.value)}
      />
    );
  }

  if (hasDiscount) {
    const finalValue = maskMoney(charge?.valueWithDiscount / 100);

    return (
      <BoxFlex
        sx={{ mb: '10px', flexDirection: 'column', alignItems: 'center' }}
      >
        <Typography fontWeight={'bold'}>{t('Amount paid')}</Typography>
        <Typography textAlign='center'>
          <Typography
            color='#b2b2b2ff'
            as='span'
            style={{ textDecoration: 'line-through' }}
          >
            {maskMoney(charge.value / 100)}
          </Typography>{' '}
          {finalValue}
        </Typography>
      </BoxFlex>
    );
  }

  const hasDifferentValues =
    charge.valuePaid && charge.valuePaid !== charge.value;

  if (hasDifferentValues) {
    return (
      <Stack spacing={1}>
        <AccountInfoLabel
          label={t('Original value')}
          text={convertToMoney(charge.value)}
        />
        <AccountInfoLabel
          label={t('Paid value')}
          text={convertToMoney(charge.valuePaid!)}
        />
      </Stack>
    );
  }

  return (
    <AccountInfoLabel
      label={t('Amount paid')}
      text={convertToMoney(charge.value)}
    />
  );
};

export default PaymentLinkCompletedQrCodeValue;
